import type { CalendarApi, DateInput } from '@fullcalendar/core';
import type FullCalendar from '@fullcalendar/react';
import { useToast } from '@src/app/notifications/toast';
import { useCallback, useContext, useMemo } from 'react';
import {
  ABS_DAY_VIEW,
  ABS_MONTH_VIEW,
  ABS_SCROLL_MONTH_VIEW,
  getAbsenceSelectionById,
  getFixedMonthVisibleRange,
  getSessionId,
  getUserWorktimeForDate,
  prepareAbsencePayload,
  userResourceId,
} from '../../lib';
import {
  type Absence,
  isAbsenceLockHeldStatus,
  type SelectedAbsence,
  type SlotData,
  type TimelineSlot,
} from '../../types';
import { TimelineContext } from '../context';
import {
  getAbsenceQueryParams,
  getHolidayRangeQueryParams,
  useAbsences,
  useHolidays,
  useResourceUsers,
} from '../query';

const GROUP_LABEL_SELECTOR = [
  '.fc-datagrid-cell-main [role="presentation"]',
  '.fc-datagrid-cell-main .MuiStack-root',
].join(', ');

export type CalendarController = {
  next: () => void;
  prev: () => void;
  today: () => void;
  getDate: () => Date | null;
  updateSize: () => void;
  select: (...args: Parameters<CalendarApi['select']>) => void;
  unselect: () => void;
  changeView: (...args: Parameters<CalendarApi['changeView']>) => void;
  gotoDate: (...args: Parameters<CalendarApi['gotoDate']>) => void;
  setMonthView: (year: number, month: number) => void;
  setMonthViewFrom: (startDate: Date) => void;
  setDayView: (date: DateInput) => void;
  setOption: (...args: Parameters<CalendarApi['setOption']>) => void;
  scrollToResource: (userId: number) => void;
};

export default function useTimeline() {
  const toast = useToast();
  const timelineContext = useContext(TimelineContext);
  const {
    view,
    selectedAbsence,
    setSelectedAbsence,
    selectedSlot,
    setSelectedSlot,
    sidebarMode,
    setSidebarMode,
    setSuppressSelectionUntil,
    selectedUserId,
    setSelectedUserId,
    closeEditForm,
    closeAbsenceDetails,
    calendarRef,
    visibleRange,
    absenceLock,
    showLegend,
    openLegend,
    closeLegend,
    showBalanceHistory,
    openBalanceHistory,
    closeBalanceHistory,
    ...restOfContext
  } = timelineContext;
  const absenceQueryParams = useMemo(
    () => getAbsenceQueryParams(visibleRange),
    [visibleRange],
  );
  const {
    absences,
    bookAbsence,
    deleteAbsences,
    requestAbsences,
    approveAbsences,
    approveHrAbsences,
    rejectAbsences,
    rejectHrAbsences,
    ...restAbsencesQuery
  } = useAbsences(absenceQueryParams);
  const {
    users,
    isFetching: isUsersFetching,
    isFetched: isUsersFetched,
  } = useResourceUsers();
  const holidayQueryParams = useMemo(
    () => getHolidayRangeQueryParams(visibleRange),
    [visibleRange],
  );
  const { holidays } = useHolidays(holidayQueryParams);

  const attachCalendar = useCallback(
    (instance: FullCalendar | null) => {
      calendarRef.current = instance;
    },
    [calendarRef],
  );

  const getApi = useCallback(
    () => calendarRef.current?.getApi() ?? null,
    [calendarRef],
  );
  const calendar = useMemo<CalendarController>(
    () => ({
      next: () => getApi()?.next(),
      prev: () => getApi()?.prev(),
      today: () => getApi()?.today(),
      getDate: () => getApi()?.getDate() ?? null,
      updateSize: () => getApi()?.updateSize(),
      select: (...args) => getApi()?.select(...args),
      unselect: () => getApi()?.unselect(),
      changeView: (...args) => getApi()?.changeView(...args),
      gotoDate: (...args) => getApi()?.gotoDate(...args),
      setMonthView: (year, month) => {
        getApi()?.changeView(ABS_MONTH_VIEW, new Date(year, month, 1));
      },
      setMonthViewFrom: (startDate: Date) => {
        const { start, end } = getFixedMonthVisibleRange(startDate);
        getApi()?.changeView(ABS_SCROLL_MONTH_VIEW, startDate);
        getApi()?.setOption('visibleRange', { start, end });
      },
      setDayView: (date) => getApi()?.changeView(ABS_DAY_VIEW, date),
      setOption: (...args) => getApi()?.setOption(...args),
      scrollToResource: (userId: number) => {
        const resourceId = userResourceId(userId);
        if (!resourceId) return;
        requestAnimationFrame(() => {
          const row = document.querySelector(
            `[data-resource-id="${resourceId}"]`,
          ) as HTMLElement | null;
          row?.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
          });
        });
      },
    }),
    [getApi],
  );

  const expandAndScrollToResource = useCallback(
    (userId: number) => {
      const resourceId = userResourceId(userId);
      if (!resourceId) return;

      const existsInDom = document.querySelector(
        `[data-resource-id="${resourceId}"]`,
      );

      if (existsInDom) {
        calendar.scrollToResource(userId);
        return;
      }

      const targetUser = users.find((u) => u.id === userId);
      const teamName = targetUser?.team?.name;
      if (!teamName) return;

      const groupRows = document.querySelectorAll('.absence-group-row');
      for (const groupCell of groupRows) {
        const label = groupCell.textContent ?? '';
        if (label.includes(teamName)) {
          const groupLabelEl = groupCell.querySelector(
            GROUP_LABEL_SELECTOR,
          ) as HTMLElement | null;
          groupLabelEl?.click();
          requestAnimationFrame(() => {
            calendar.updateSize();
            calendar.scrollToResource(userId);
          });
          return;
        }
      }
    },
    [calendar, users],
  );

  const viewType = view?.type ?? ABS_MONTH_VIEW;
  const isMonthView = useMemo(
    () => [ABS_MONTH_VIEW, ABS_SCROLL_MONTH_VIEW].includes(viewType),
    [viewType],
  );
  const isDayView = useMemo(() => viewType === ABS_DAY_VIEW, [viewType]);

  const lockHeaders = useMemo(() => {
    if (!isAbsenceLockHeldStatus(absenceLock.status)) {
      return undefined;
    }
    return {
      'X-Absence-Session-Id': getSessionId(),
    };
  }, [absenceLock.status]);

  const isSelectedUserLocked = useMemo(() => {
    const selectedUserId = selectedSlot?.userId ?? null;
    return (
      !!selectedUserId &&
      isAbsenceLockHeldStatus(absenceLock.status) &&
      absenceLock.lock?.userId === selectedUserId
    );
  }, [absenceLock.lock?.userId, absenceLock.status, selectedSlot?.userId]);

  const book = useCallback(
    async (values: Record<string, unknown>) => {
      const isUpdate = typeof values.absenceId === 'number';
      return bookAbsence(
        {
          ...prepareAbsencePayload(values),
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeEditForm();
            toast.success(
              isUpdate
                ? 'Absence updated successfully'
                : 'Absence created successfully',
            );
            openBalanceHistory();
          },
        },
      );
    },
    [bookAbsence, closeEditForm, lockHeaders, openBalanceHistory, toast],
  );

  const remove = useCallback(
    async (ids: number[]) => {
      return deleteAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeEditForm();
            toast.success('Absence deleted successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [closeEditForm, deleteAbsences, lockHeaders, openBalanceHistory, toast],
  );

  const request = useCallback(
    async (ids: number[]) => {
      return requestAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Absence requested successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      closeAbsenceDetails,
      lockHeaders,
      openBalanceHistory,
      requestAbsences,
      toast,
    ],
  );

  const approve = useCallback(
    async (ids: number[]) => {
      return approveAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Absence approved successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      approveAbsences,
      closeAbsenceDetails,
      lockHeaders,
      openBalanceHistory,
      toast,
    ],
  );

  const reject = useCallback(
    async (ids: number[]) => {
      return rejectAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Absence rejected successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      closeAbsenceDetails,
      lockHeaders,
      openBalanceHistory,
      rejectAbsences,
      toast,
    ],
  );

  const approveHr = useCallback(
    async (ids: number[]) => {
      return approveHrAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Absence approved successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      approveHrAbsences,
      closeAbsenceDetails,
      lockHeaders,
      openBalanceHistory,
      toast,
    ],
  );

  const rejectHr = useCallback(
    async (ids: number[]) => {
      return rejectHrAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Absence rejected successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      closeAbsenceDetails,
      lockHeaders,
      openBalanceHistory,
      rejectHrAbsences,
      toast,
    ],
  );

  const selectedAbsencesById = useMemo<Map<number, Absence>>(
    () => new Map(absences.map((absence: Absence) => [absence.id, absence])),
    [absences],
  );

  const isDifferentAbsencesSelected = useCallback(
    (ids: number[] | null | undefined) => {
      if (!ids || ids.length <= 1) {
        return false;
      }

      let firstType: number | undefined;
      let firstApprovedManager: boolean | null | undefined;
      let firstApprovedHr: boolean | null | undefined;
      for (const id of ids) {
        const obj = selectedAbsencesById.get(id);
        if (!obj) {
          continue;
        }
        if (obj.typeId == null) {
          return true;
        }
        if (firstType === undefined) {
          firstType = obj.typeId;
          firstApprovedManager = obj.approvedManager;
          firstApprovedHr = obj.approvedHr;
        } else if (obj.typeId !== firstType) {
          return true;
        } else if (obj.approvedManager !== firstApprovedManager) {
          return true;
        } else if (obj.approvedHr !== firstApprovedHr) {
          return true;
        }
      }
      return false;
    },
    [selectedAbsencesById],
  );

  const getAbsenceTimeRange = useCallback(
    (slot: TimelineSlot) => {
      let startTime = slot.startTime;
      let endTime = slot.endTime;
      if (slot.userId && slot.start && slot.end) {
        const userWorktime = getUserWorktimeForDate(
          slot.userId,
          users,
          slot.start,
        );
        if (userWorktime && !slot.startTime && !slot.endTime) {
          startTime = userWorktime.start;
          endTime = userWorktime.end;
        }
      }
      return {
        startTime,
        endTime,
      };
    },
    [users],
  );

  const selectedSlotData = useMemo((): SlotData | null => {
    if (!selectedSlot) {
      return null;
    }

    const { selectedIds: selectedAbsencesIds, start, end } = selectedSlot;
    const selectedData = {
      start,
      end,
      typeId: null,
      ...(isDayView && getAbsenceTimeRange(selectedSlot)),
      userId: selectedSlot.userId,
      tentative: false,
      notes: '',
    };

    if (isDayView) {
      if (!selectedSlot.editedAbsenceId) {
        return selectedData;
      }

      const absence = absences.find(
        (a) => a.id === selectedSlot.editedAbsenceId,
      );
      if (absence) {
        return {
          ...selectedData,
          typeId: absence.typeId ?? null,
          tentative: absence.absenceTentative ?? false,
          notes: absence.notes ?? '',
        };
      }
      return selectedData;
    }

    if (
      isDifferentAbsencesSelected(selectedAbsencesIds) ||
      !selectedAbsencesIds?.length
    ) {
      return selectedData;
    }

    const selection = getAbsenceSelectionById(selectedAbsencesIds[0], absences);
    if (selection?.absenceIds.length) {
      const absenceId = selection.absenceIds[0];
      const absence = absences.find((a) => a.id === absenceId);
      if (absence) {
        return {
          ...selectedData,
          typeId: absence.typeId ?? null,
          tentative: absence.absenceTentative ?? false,
          notes: absence.notes ?? '',
        };
      }
    }
    return selectedData;
  }, [
    absences,
    selectedSlot,
    isDifferentAbsencesSelected,
    getAbsenceTimeRange,
    isDayView,
  ]);

  const focusAbsence = useCallback(
    (selection: SelectedAbsence, anchorDate: Date) => {
      setSuppressSelectionUntil(Date.now() + 500);

      if (isDayView) {
        calendar.setDayView(anchorDate);
      } else {
        const monthStart = new Date(
          anchorDate.getFullYear(),
          anchorDate.getMonth(),
          1,
        );
        calendar.setMonthViewFrom(monthStart);
      }

      window.requestAnimationFrame(() => {
        setSidebarMode('details');
        setSelectedUserId(selection.userId);
        setSelectedAbsence(selection);
        setSelectedSlot(null);
      });
    },
    [
      calendar,
      isDayView,
      setSelectedAbsence,
      setSidebarMode,
      setSuppressSelectionUntil,
      setSelectedSlot,
      setSelectedUserId,
    ],
  );

  const timeline = {
    absenceLock,
    calendar,
    closeEditForm,
    closeAbsenceDetails,
    view,
    selectedAbsence,
    setSelectedAbsence,
    selectedSlot,
    setSelectedSlot,
    sidebarMode,
    setSidebarMode,
    selectedUserId,
    setSelectedUserId,
    attachCalendar,
    ...restOfContext,
    ...restAbsencesQuery,
    absences,
    holidays,
    users,
    isUsersFetching,
    isUsersFetched,
    visibleRange,
    book,
    selectedSlotData,
    remove,
    request,
    approve,
    reject,
    approveHr,
    rejectHr,
    isMonthView,
    isDayView,
    isSelectedUserLocked,
    showLegend,
    openLegend,
    closeLegend,
    showBalanceHistory,
    openBalanceHistory,
    closeBalanceHistory,
    expandAndScrollToResource,
    focusAbsence,
  };
  return timeline;
}
